import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { detalharAbertura } from '../api/aberturas.js';
import type {
  AberturaCancelada,
  DecisaoDaDivergencia,
  DetalheDaAbertura,
  DivergenciaDecidida,
  EntregaDaAbertura,
} from '../api/aberturas.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import { Botao } from '../componentes/Botao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { formatarDataHora } from '../componentes/datas.js';
import { rotuloDoLocal } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { IconeMovimentacoes } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { CartaoDeDivergencia } from './movimentacoes/CartaoDeDivergencia.js';
import { DialogoDeCancelamento } from './movimentacoes/DialogoDeCancelamento.js';
import { DialogoDeDecisao, estaEmDivergencia } from './movimentacoes/DialogoDeDecisao.js';
import { rotuloDoStatus, tomDoStatus } from './movimentacoes/familia.js';
import { formatarQuantidade } from './movimentacoes/quantidade.js';
import { Quantidade, Saldo } from './movimentacoes/SaldoDaAbertura.js';
import {
  aceitaCancelamento,
  rotuloDaSituacao,
  tomDaSituacao,
} from './movimentacoes/situacao-da-abertura.js';
import './DetalheAbertura.css';

const COLUNAS: readonly Coluna<EntregaDaAbertura>[] = [
  {
    chave: 'sequencia',
    rotulo: 'Nº',
    largura: '64px',
    celula: (entrega) => <span className="mono">{entrega.sequencia}</span>,
  },
  {
    chave: 'registradoEm',
    rotulo: 'Entregue em',
    largura: '140px',
    celula: (entrega) => (
      <span className="movimentacoes__horario">{formatarDataHora(entrega.registradoEm)}</span>
    ),
  },
  {
    chave: 'destino',
    rotulo: 'Destino',
    largura: '15%',
    celula: (entrega) => (
      <span className="mono">{rotuloDoLocal(entrega.filialDestino, entrega.depositoDestino)}</span>
    ),
  },
  {
    chave: 'quantidade',
    rotulo: 'Quantidade',
    largura: '120px',
    alinhamento: 'direita',
    celula: (entrega) => (
      <span className="detalhe-abertura__quantidade">
        <Quantidade rotulo="Quantidade enviada" valor={entrega.quantidade} />
        {entrega.quantidadeInformada !== null && (
          <span className="detalhe-abertura__informada">
            informada {formatarQuantidade(entrega.quantidadeInformada)}
          </span>
        )}
      </span>
    ),
  },
  {
    chave: 'status',
    rotulo: 'No ERP',
    largura: '140px',
    celula: (entrega) => (
      <ChipStatus
        tom={tomDoStatus(entrega.status)}
        rotulo={rotuloDoStatus(entrega.status)}
        tamanho="tabela"
      />
    ),
  },
  {
    chave: 'mensagem',
    rotulo: 'Mensagem do ERP',
    celula: (entrega) =>
      entrega.mensagemErp === null ? (
        <span className="detalhe-abertura__sem-mensagem">—</span>
      ) : (
        <span
          className={
            entrega.status === 'Rejeitada'
              ? 'detalhe-abertura__mensagem detalhe-abertura__mensagem--erro'
              : 'detalhe-abertura__mensagem'
          }
        >
          {entrega.mensagemErp}
        </span>
      ),
  },
  {
    chave: 'autor',
    rotulo: 'Entregue por',
    largura: '15%',
    celula: (entrega) => (
      <span className="detalhe-abertura__autor">
        {entrega.operador === null
          ? 'Sem operador'
          : `${entrega.operador.nome} · ${entrega.operador.matricula}`}
        <span className="detalhe-abertura__aparelho">{entrega.dispositivo.identificador}</span>
      </span>
    ),
  },
];

type Acao =
  | { readonly tipo: 'cancelar' }
  | { readonly tipo: 'decidir'; readonly decisao: DecisaoDaDivergencia };

/**
 * Detalhe de uma movimentação em duas etapas (r1-mov).
 *
 * O início (produto, origem, quem, quando, quanto) e cada entrega em ordem,
 * com o destino dela, o status da operação no caminho até o ERP e a mensagem
 * do ERP quando houver — só o texto do retorno, nunca a resposta bruta.
 *
 * As duas ações do painel moram aqui, e só para Administrador e Supervisor:
 * decidir a divergência (quando há entrega retida) e cancelar o saldo pendente
 * (quando ainda se espera entrega). As entregas feitas não se desfazem.
 */
export function DetalheAbertura() {
  const { id } = useParams();
  const buscar = useMemo(() => (id === undefined ? null : () => detalharAbertura(id)), [id]);
  const consulta = useConsulta(buscar);
  const [acao, definirAcao] = useState<Acao | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);
  const pode = usePode();

  const detalhe = consulta.dados;
  const podeCancelar =
    detalhe !== null && pode('operacoes.agir') && aceitaCancelamento(detalhe.status);

  function concluir(texto: string) {
    definirAcao(null);
    definirAviso(texto);
    consulta.recarregar();
  }

  return (
    <Pagina
      voltarPara="/movimentacoes"
      contexto="Movimentações · em duas etapas"
      titulo={detalhe === null ? 'Movimentação' : `Movimentação de ${detalhe.produto}`}
      {...(podeCancelar
        ? {
            acoes: (
              <Botao
                variante="destrutivo"
                altura="media"
                onClick={() => {
                  definirAviso(null);
                  definirAcao({ tipo: 'cancelar' });
                }}
              >
                Cancelar saldo pendente
              </Botao>
            ),
          }
        : {})}
    >
      {aviso !== null && <AvisoDaTela tom="ok">{aviso}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando a movimentação…"
        estaVazio={() => false}
        vazio={null}
      >
        {(dados) => (
          <Conteudo
            detalhe={dados}
            aoEscolher={(decisao) => {
              definirAviso(null);
              definirAcao({ tipo: 'decidir', decisao });
            }}
          />
        )}
      </Resultado>

      {detalhe !== null && acao?.tipo === 'cancelar' && (
        <DialogoDeCancelamento
          abertura={detalhe}
          aoCancelar={(cancelada: AberturaCancelada) => {
            concluir(
              `Saldo pendente cancelado: ${formatarQuantidade(cancelada.pendenteCancelado)} deixaram de existir.`,
            );
          }}
          aoVoltar={() => {
            definirAcao(null);
          }}
        />
      )}

      {detalhe !== null && acao?.tipo === 'decidir' && estaEmDivergencia(detalhe) && (
        <DialogoDeDecisao
          abertura={detalhe}
          decisao={acao.decisao}
          aoDecidir={(decidida: DivergenciaDecidida) => {
            concluir(
              `Divergência decidida: a entrega vai ao ERP com ${formatarQuantidade(decidida.entrega.quantidade)}.`,
            );
          }}
          aoCancelar={() => {
            definirAcao(null);
          }}
        />
      )}
    </Pagina>
  );
}

function Conteudo({
  detalhe,
  aoEscolher,
}: {
  readonly detalhe: DetalheDaAbertura;
  readonly aoEscolher: (decisao: DecisaoDaDivergencia) => void;
}) {
  return (
    <div className="detalhe-abertura">
      <Secao titulo="Início">
        <Inicio detalhe={detalhe} />
      </Secao>

      {estaEmDivergencia(detalhe) && (
        <Secao titulo="Divergência de quantidade">
          <CartaoDeDivergencia abertura={detalhe} aoEscolher={aoEscolher} comLink={false} />
        </Secao>
      )}

      <Secao titulo="Entregas">
        {detalhe.entregas.length === 0 ? (
          <EstadoVazio
            tom="neutro"
            icone={<IconeMovimentacoes tamanho={28} />}
            titulo="Nenhuma entrega ainda."
            detalhe="Cada entrega que o operador registrar no destino aparece aqui, com o status no ERP."
          />
        ) : (
          <Tabela
            itens={detalhe.entregas}
            colunas={COLUNAS}
            chaveDe={(entrega) => entrega.id}
            descricao="Entregas da movimentação"
          />
        )}
      </Secao>
    </div>
  );
}

function Secao({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  const idTitulo = useId();

  return (
    <section className="detalhe-abertura__secao" aria-labelledby={idTitulo}>
      <h2 className="detalhe-abertura__titulo" id={idTitulo}>
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Inicio({ detalhe }: { readonly detalhe: DetalheDaAbertura }) {
  const { operador, dispositivo } = detalhe;

  return (
    <div className="detalhe-abertura__cartao">
      <div className="detalhe-abertura__topo">
        <ChipStatus tom={tomDaSituacao(detalhe.status)} rotulo={rotuloDaSituacao(detalhe.status)} />
        <Saldo
          itens={[
            { rotulo: 'Iniciada', valor: detalhe.quantidade },
            { rotulo: 'Entregue', valor: detalhe.entregue },
            { rotulo: 'Pendente', valor: detalhe.pendente },
          ]}
        />
      </div>

      <dl className="detalhe-abertura__fatos">
        <Fato rotulo="Produto">
          <span className="mono">{detalhe.produto}</span>
        </Fato>
        {detalhe.derivacao !== null && (
          <Fato rotulo="Derivação">
            <span className="mono">{detalhe.derivacao}</span>
          </Fato>
        )}
        <Fato rotulo="Origem">
          <span className="mono">
            {rotuloDoLocal(detalhe.filialOrigem, detalhe.depositoOrigem)}
          </span>
        </Fato>
        <Fato rotulo="Iniciada em">{formatarDataHora(detalhe.registradoEm)}</Fato>
        <Fato rotulo="Operador">
          {operador.nome} · {operador.matricula}
        </Fato>
        <Fato rotulo="Aparelho">
          {dispositivo.apelido === null ? (
            <span className="mono">{dispositivo.identificador}</span>
          ) : (
            <>
              {dispositivo.apelido} · <span className="mono">{dispositivo.identificador}</span>
            </>
          )}
        </Fato>
        {detalhe.observacao !== null && <Fato rotulo="Observação">{detalhe.observacao}</Fato>}
      </dl>
    </div>
  );
}

function Fato({ rotulo, children }: { readonly rotulo: string; readonly children: ReactNode }) {
  return (
    <div className="detalhe-abertura__fato">
      <dt>{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}
